/**
 * NOS-178: Poll an import_jobs row for live import progress (M21.1).
 * Imports run as chained edge-function invocations updating the job row;
 * the UI polls (2.5s) while the job is live and stops on terminal states.
 * NOS-192: `review` pauses polling; continueImportJob/stopImportJob drive the
 * preview gate. NOS-190: report helpers read the persisted import report.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { supabase } from "@/lib/supabase"
import { errorMessage } from "@/lib/errorMessage"
import type { Tables } from "@/types/database"

export type ImportJobRow = Tables<"import_jobs">

export type ImportJobStatus =
  | "queued"
  | "crawling"
  | "mapping"
  | "review"
  | "failed"
  | "done"

const ACTIVE_STATUSES: ImportJobStatus[] = ["queued", "crawling", "mapping"]

export const IMPORT_JOB_POLL_MS = 2500

export function isImportJobActive(status: string | null | undefined): boolean {
  return ACTIVE_STATUSES.includes(status as ImportJobStatus)
}

export async function fetchImportJob(jobId: string): Promise<ImportJobRow | null> {
  const { data, error } = await supabase
    .from("import_jobs")
    .select("*")
    .eq("id", jobId)
    .maybeSingle()
  if (error) return null
  return data
}

// === NOS-192: preview gate (review pause) ===

/** Review pauses older than this finalize as sample-only (mirrors the server TTL). */
export const IMPORT_REVIEW_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** True when a review pause is past its TTL (server will only allow "stop"). */
export function isImportReviewExpired(
  job: Pick<ImportJobRow, "status" | "updated_at"> | null,
  now: number = Date.now()
): boolean {
  if (!job || job.status !== "review") return false
  const t = Date.parse(job.updated_at ?? "")
  if (!Number.isFinite(t)) return false
  return now - t > IMPORT_REVIEW_TTL_MS
}

/** True when the job is paused at the preview gate and still actionable. */
export function isImportJobAwaitingReview(
  job: Pick<ImportJobRow, "status" | "updated_at"> | null
): boolean {
  return job?.status === "review" && !isImportReviewExpired(job)
}

async function invokeReviewAction(
  jobId: string,
  action: "continue" | "stop"
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data: sessWrap } = await supabase.auth.getSession()
    const token = sessWrap.session?.access_token
    const { data, error } = await supabase.functions.invoke("crawl-site", {
      body: { action, job_id: jobId },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    })
    if (error) {
      return { ok: false, error: error.message ?? "Request failed. Please try again." }
    }
    const payload = data as { ok?: boolean; error?: string } | null
    if (payload?.error) return { ok: false, error: payload.error }
    return { ok: true }
  } catch (err) {
    return {
      ok: false,
      error: errorMessage(err, "Request failed. Please try again."),
    }
  }
}

/** "Import everything" — resume stage 2 of a review-paused import. */
export function continueImportJob(jobId: string): Promise<{ ok: boolean; error?: string }> {
  return invokeReviewAction(jobId, "continue")
}

/** "Stop here" — finalize a review-paused import with only the sample. */
export function stopImportJob(jobId: string): Promise<{ ok: boolean; error?: string }> {
  return invokeReviewAction(jobId, "stop")
}

/**
 * Poll a specific import job while it is live. Returns the latest row and
 * whether it is still active. Passing null disables polling. `refresh`
 * restarts polling — call it after resuming a review-paused job.
 * Expired review pauses are finalized sample-only (best effort) on sight.
 */
export function useImportJob(
  jobId: string | null,
  pollMs: number = IMPORT_JOB_POLL_MS
): { job: ImportJobRow | null; isActive: boolean; refresh: () => void } {
  const [job, setJob] = useState<ImportJobRow | null>(null)
  const [pollNonce, setPollNonce] = useState(0)
  const jobRef = useRef<ImportJobRow | null>(null)
  const expiredHandledRef = useRef<string | null>(null)

  useEffect(() => {
    if (!jobId) {
      jobRef.current = null
      setJob(null)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = async () => {
      let row = await fetchImportJob(jobId)
      if (cancelled) return
      // NOS-192: an abandoned review pause finalizes as sample-only — stage 2
      // never runs without a fresh approval. Best effort; owner/admin only.
      if (row && isImportReviewExpired(row) && expiredHandledRef.current !== row.id) {
        expiredHandledRef.current = row.id
        await stopImportJob(row.id).catch(() => undefined)
        const updated = await fetchImportJob(jobId)
        if (cancelled) return
        if (updated) row = updated
      }
      if (row) {
        jobRef.current = row
        setJob(row)
      }
      if (!row || isImportJobActive(row.status)) {
        timer = setTimeout(tick, pollMs)
      }
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [jobId, pollMs, pollNonce])

  const refresh = useCallback(() => setPollNonce((n) => n + 1), [])

  return { job, isActive: isImportJobActive(job?.status), refresh }
}

/** NOS-179: shape of a failed image download recorded on the job result. */
export interface ImportImageFailure {
  url: string
  reason: string
  slug?: string
  /** NOS-235: machine-readable failure class (absent on pre-NOS-235 rows). */
  code?: string
}

/** NOS-179: failed image downloads from a job's result (empty when none). */
export function imageFailuresFromJob(job: ImportJobRow | null): ImportImageFailure[] {
  const raw = (job?.result as { image_failures?: unknown } | null)?.image_failures
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (f): f is ImportImageFailure =>
      f != null &&
      typeof f === "object" &&
      typeof (f as ImportImageFailure).url === "string" &&
      typeof (f as ImportImageFailure).reason === "string"
  )
}

// === NOS-235: "Retry failed images" ===

/**
 * NOS-235: failures the manual retry button will attempt. Everything is
 * retryable except the SVG policy exclusion (legacy rows match by reason).
 */
export function retryableImageFailureCount(failures: ImportImageFailure[]): number {
  return failures.filter(
    (f) => f.code !== "svg_excluded" && !/^SVG images are not imported/i.test(f.reason)
  ).length
}

/** NOS-235: live progress of an image-retry pass, written to job state. */
export interface ImportImageRetryState {
  status: "running" | "done" | "failed"
  total: number
  attempted: number
  recovered: number
}

/** NOS-235: the retry-pass progress from a job's state, or null. */
export function imageRetryStateFromJob(job: ImportJobRow | null): ImportImageRetryState | null {
  const raw = (job?.state as { image_retry?: unknown } | null)?.image_retry
  if (raw == null || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  if (r.status !== "running" && r.status !== "done" && r.status !== "failed") return null
  const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0)
  return {
    status: r.status,
    total: num(r.total),
    attempted: num(r.attempted),
    recovered: num(r.recovered),
  }
}

export interface RetryFailedImagesResult {
  ok: boolean
  error?: string
  attempted?: number
  recovered?: number
  remaining?: number
}

/**
 * NOS-235: invoke the manual retry over a job's failed image downloads.
 * Resolves when the pass finishes (the function runs it synchronously);
 * callers poll the job row meanwhile for state.image_retry progress.
 */
export async function retryFailedImages(jobId: string): Promise<RetryFailedImagesResult> {
  try {
    const { data: sessWrap } = await supabase.auth.getSession()
    const token = sessWrap.session?.access_token
    const { data, error } = await supabase.functions.invoke("retry-failed-images", {
      body: { job_id: jobId },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    })
    if (error) {
      return { ok: false, error: error.message ?? "Retry failed. Please try again." }
    }
    const payload = data as {
      ok?: boolean
      error?: string
      attempted?: number
      recovered?: number
      remaining?: number
    } | null
    if (payload?.error) return { ok: false, error: payload.error }
    return {
      ok: true,
      attempted: typeof payload?.attempted === "number" ? payload.attempted : 0,
      recovered: typeof payload?.recovered === "number" ? payload.recovered : 0,
      remaining: typeof payload?.remaining === "number" ? payload.remaining : undefined,
    }
  } catch (err) {
    return {
      ok: false,
      error: errorMessage(err, "Retry failed. Please try again."),
    }
  }
}

/** NOS-186: imported-content counts by type, from a done job's result. */
export interface ImportContentSummary {
  pages: number
  posts: number
}

/**
 * NOS-186: counts of imported pages vs blog posts from a job result.
 * Prefers the result's `counts_by_type`; falls back to counting result rows
 * (rows without a content_type are legacy page rows). Null when the job has
 * no results yet.
 */
export function contentSummaryFromJob(job: ImportJobRow | null): ImportContentSummary | null {
  const result = job?.result as {
    counts_by_type?: { page?: unknown; post?: unknown }
    pages?: unknown
  } | null
  if (!result) return null
  const counts = result.counts_by_type
  if (
    counts &&
    typeof counts === "object" &&
    typeof counts.page === "number" &&
    typeof counts.post === "number"
  ) {
    return { pages: counts.page, posts: counts.post }
  }
  if (!Array.isArray(result.pages)) return null
  let pages = 0
  let posts = 0
  for (const row of result.pages) {
    if (row == null || typeof row !== "object") continue
    const r = row as { section_count?: unknown; content_type?: unknown; skipped?: unknown }
    if (typeof r.section_count !== "number" || r.skipped) continue
    if (r.content_type === "post") posts++
    else pages++
  }
  return { pages, posts }
}

/**
 * NOS-198: a page the mapper deliberately skipped (e.g. the blog index route,
 * which is served by the CMS listing page rather than imported as sections).
 * `reason` is the raw `skipped` value from map-site-content results.
 */
export interface ImportSkippedPage {
  slug: string
  reason: string
}

/**
 * NOS-198: user-facing copy for a mapper skip reason. Blog-index rows read as a
 * deliberate skip ("served by your blog") rather than a failed "0 sections".
 */
export function skipReasonLabel(reason: string | null | undefined): string {
  if (reason === "blog_index") return "Skipped — served by your blog"
  // NOS-227: password-protected source pages import as unpublished drafts
  if (reason === "password_protected") return "Imported as draft (password-protected)"
  return "Skipped"
}

/**
 * NOS-198: pages the mapper skipped, read from a job result (`result.pages[]`
 * rows carrying a `skipped` reason). Empty when the job skipped nothing.
 */
export function skippedPagesFromJob(job: ImportJobRow | null): ImportSkippedPage[] {
  const raw = (job?.result as { pages?: unknown } | null)?.pages
  if (!Array.isArray(raw)) return []
  const out: ImportSkippedPage[] = []
  for (const row of raw) {
    if (row == null || typeof row !== "object") continue
    const r = row as { slug?: unknown; skipped?: unknown }
    if (typeof r.slug === "string" && typeof r.skipped === "string" && r.skipped) {
      out.push({ slug: r.slug, reason: r.skipped })
    }
  }
  return out
}

// === NOS-253: password-gated page recovery ===

/** A page imported as a protected draft stub, recoverable with its password. */
export interface ImportProtectedPage {
  slug: string
  /** Source URL (from result.source_urls) — display context for the card. */
  url?: string
}

/**
 * NOS-253: pages still imported as password-protected draft stubs, from the
 * job result rows. These are the recovery card's targets; a recovered page's
 * row loses its `skipped` marker and drops off this list.
 */
export function protectedPagesFromJob(job: ImportJobRow | null): ImportProtectedPage[] {
  const result = job?.result as { pages?: unknown; source_urls?: unknown } | null
  const raw = result?.pages
  if (!Array.isArray(raw)) return []
  const sourceUrls =
    result?.source_urls != null &&
    typeof result.source_urls === "object" &&
    !Array.isArray(result.source_urls)
      ? (result.source_urls as Record<string, unknown>)
      : {}
  const out: ImportProtectedPage[] = []
  for (const row of raw) {
    if (row == null || typeof row !== "object") continue
    const r = row as { slug?: unknown; skipped?: unknown }
    if (typeof r.slug !== "string" || r.skipped !== "password_protected") continue
    const url = sourceUrls[r.slug]
    out.push({ slug: r.slug, ...(typeof url === "string" ? { url } : {}) })
  }
  return out
}

/** NOS-253: live progress of a recovery pass (state.protected_recovery). */
export interface ImportProtectedRecoveryState {
  /** running = unlock scrapes; mapping = stub replacement hops; done = settled */
  status: "running" | "mapping" | "done"
  recovered: string[]
  failed: Array<{ slug: string; reason: string }>
  pending: string[]
  total: number
}

/** NOS-253: the recovery-pass progress from a job's state, or null. */
export function protectedRecoveryFromJob(
  job: ImportJobRow | null
): ImportProtectedRecoveryState | null {
  const raw = (job?.state as { protected_recovery?: unknown } | null)?.protected_recovery
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  if (r.status !== "running" && r.status !== "mapping" && r.status !== "done") return null
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : []
  const failed = Array.isArray(r.failed)
    ? r.failed.filter(
        (f): f is { slug: string; reason: string } =>
          f != null &&
          typeof f === "object" &&
          typeof (f as { slug?: unknown }).slug === "string" &&
          typeof (f as { reason?: unknown }).reason === "string"
      )
    : []
  return {
    status: r.status,
    recovered: strings(r.recovered),
    failed,
    pending: strings(r.pending),
    total: typeof r.total === "number" && Number.isFinite(r.total) ? r.total : 0,
  }
}

/** NOS-253: per-page outcome of an unlock request. */
export type RecoverPageStatus =
  | "unlocked"
  | "wrong_password"
  | "attempts_exhausted"
  | "error"
  | "not_attempted"
  | "no_password"

export interface RecoverProtectedPagesResult {
  ok: boolean
  error?: string
  pages?: Array<{ slug: string; status: RecoverPageStatus; error?: string }>
  /** True when unlocked pages were handed to the mapping hops (poll the job). */
  mapping?: boolean
}

/**
 * NOS-253: unlock password-gated pages and replace their draft stubs. The
 * password travels ONLY in this request body — it is never persisted, so
 * there is nothing to clean up client- or server-side afterwards.
 */
export async function recoverProtectedPages(params: {
  jobId: string
  password?: string
  pagePasswords?: Record<string, string>
  slugs?: string[]
}): Promise<RecoverProtectedPagesResult> {
  try {
    const { data: sessWrap } = await supabase.auth.getSession()
    const token = sessWrap.session?.access_token
    const { data, error } = await supabase.functions.invoke("recover-protected-pages", {
      body: {
        job_id: params.jobId,
        ...(params.password ? { password: params.password } : {}),
        ...(params.pagePasswords && Object.keys(params.pagePasswords).length > 0
          ? { page_passwords: params.pagePasswords }
          : {}),
        ...(params.slugs && params.slugs.length > 0 ? { slugs: params.slugs } : {}),
      },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    })
    if (error) {
      return { ok: false, error: error.message ?? "Recovery failed. Please try again." }
    }
    const payload = data as RecoverProtectedPagesResult | null
    if (payload?.error) return { ok: false, error: payload.error }
    return {
      ok: true,
      pages: Array.isArray(payload?.pages) ? payload.pages : [],
      mapping: payload?.mapping === true,
    }
  } catch (err) {
    return {
      ok: false,
      error: errorMessage(err, "Recovery failed. Please try again."),
    }
  }
}

/** NOS-192: what stage 2 will import, shown while the job is paused at review. */
export interface ImportRemainingSummary {
  pages: number
  posts: number
}

/**
 * NOS-192: remaining pages/posts from a review-paused job's result
 * (`result.remaining`, written at the pause). Null when absent.
 */
export function remainingSummaryFromJob(job: ImportJobRow | null): ImportRemainingSummary | null {
  const raw = (job?.result as { remaining?: { pages?: unknown; posts?: unknown } } | null)
    ?.remaining
  if (raw != null && typeof raw.pages === "number" && typeof raw.posts === "number") {
    return { pages: raw.pages, posts: raw.posts }
  }
  return null
}

/** NOS-190: the persisted import report (mirrors _shared/importReport.ts). */
export interface ImportJobReport {
  imported: { pages: number; posts: number }
  skipped_over_cap: { pages: number; articles: number; example_urls: string[] }
  failed_crawls: string[]
  failed_maps: Array<{ slug: string; reason: string }>
  image_failure_count: number
  /** NOS-229: no homepage landed among the imported pages — loudest flag in the report. */
  homepage_missing: boolean
  /** NOS-241: redirect rows written so stale source URLs land on the new pages (informational). */
  redirects_created: number
  /** NOS-232: nav-target pages that shipped empty (source page failed to crawl / over cap). */
  empty_pages: string[]
  /** NOS-227: password-protected source pages imported as unpublished draft stubs (slugs). */
  protected_drafts: string[]
  /** NOS-237: source videos whose embed type NOS can't play — not imported, never silent. */
  unsupported_videos: string[]
  /** NOS-217: footer background not detected — default dark footer in use (informational). */
  footer_background_missing: boolean
  import_design: boolean
  page_cap: number
  sources: Record<string, string>
}

/**
 * NOS-190: the import report from a job's result. Defensive: any missing or
 * malformed piece degrades to an empty value rather than dropping the report.
 * Null when the job has no report (legacy jobs).
 */
export function importReportFromJob(job: ImportJobRow | null): ImportJobReport | null {
  const raw = (job?.result as { report?: unknown } | null)?.report
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>

  const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0)
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : []

  const imported = (r.imported ?? {}) as Record<string, unknown>
  const skipped = (r.skipped_over_cap ?? {}) as Record<string, unknown>
  const failedMaps = Array.isArray(r.failed_maps)
    ? r.failed_maps.filter(
        (f): f is { slug: string; reason: string } =>
          f != null &&
          typeof f === "object" &&
          typeof (f as { slug?: unknown }).slug === "string" &&
          typeof (f as { reason?: unknown }).reason === "string"
      )
    : []
  const sources: Record<string, string> = {}
  if (r.sources != null && typeof r.sources === "object" && !Array.isArray(r.sources)) {
    for (const [k, v] of Object.entries(r.sources as Record<string, unknown>)) {
      if (typeof v === "string") sources[k] = v
    }
  }

  return {
    imported: { pages: num(imported.pages), posts: num(imported.posts) },
    skipped_over_cap: {
      pages: num(skipped.pages),
      articles: num(skipped.articles),
      example_urls: strings(skipped.example_urls),
    },
    failed_crawls: strings(r.failed_crawls),
    failed_maps: failedMaps,
    image_failure_count: num(r.image_failure_count),
    homepage_missing: r.homepage_missing === true,
    redirects_created: num(r.redirects_created),
    empty_pages: strings(r.empty_pages),
    protected_drafts: strings(r.protected_drafts),
    unsupported_videos: strings(r.unsupported_videos),
    footer_background_missing: r.footer_background_missing === true,
    import_design: r.import_design !== false,
    page_cap: num(r.page_cap),
    sources,
  }
}

// === NOS-214: guided-review decisions ===

/** NOS-214: one choice on a decision (mirrors _shared/importDecisions.ts). */
export interface ImportDecisionOption {
  id: string
  label: string
  /** Cautionary or clarifying copy shown when this option is chosen. */
  note?: string
}

/** NOS-214: a deliberate import deviation surfaced for review (mirrors _shared/importDecisions.ts). */
export interface ImportDecision {
  key: string
  title: string
  what_changed: string
  reasoning: string
  source_state: string
  nos_state: string
  options: ImportDecisionOption[]
  recommended: string
  payload?: Record<string, unknown>
}

/**
 * NOS-214: the guided-review decisions from a job's report. Defensive: rows
 * missing required copy, fewer than two options, or a recommended id that
 * doesn't match an option are dropped (never rendered half-formed).
 */
export function importDecisionsFromJob(job: ImportJobRow | null): ImportDecision[] {
  const report = (job?.result as { report?: { decisions?: unknown } } | null)?.report
  const raw = report?.decisions
  if (!Array.isArray(raw)) return []
  const out: ImportDecision[] = []
  for (const row of raw) {
    if (row == null || typeof row !== "object") continue
    const d = row as Record<string, unknown>
    const str = (v: unknown): string | null =>
      typeof v === "string" && v.trim() !== "" ? v : null
    const key = str(d.key)
    const title = str(d.title)
    const whatChanged = str(d.what_changed)
    const reasoning = str(d.reasoning)
    const sourceState = str(d.source_state)
    const nosState = str(d.nos_state)
    const recommended = str(d.recommended)
    if (!key || !title || !whatChanged || !reasoning || !sourceState || !nosState || !recommended) {
      continue
    }
    const options: ImportDecisionOption[] = Array.isArray(d.options)
      ? (d.options as unknown[]).flatMap((o) => {
          if (o == null || typeof o !== "object") return []
          const opt = o as Record<string, unknown>
          const id = str(opt.id)
          const label = str(opt.label)
          if (!id || !label) return []
          return [{ id, label, ...(str(opt.note) ? { note: str(opt.note)! } : {}) }]
        })
      : []
    if (options.length < 2) continue
    if (!options.some((o) => o.id === recommended)) continue
    const payload =
      d.payload != null && typeof d.payload === "object" && !Array.isArray(d.payload)
        ? (d.payload as Record<string, unknown>)
        : undefined
    out.push({
      key,
      title,
      what_changed: whatChanged,
      reasoning,
      source_state: sourceState,
      nos_state: nosState,
      options,
      recommended,
      ...(payload ? { payload } : {}),
    })
  }
  return out
}

/**
 * NOS-214: slugs of the blog posts this job imported (from result rows) — the
 * targeting list for the "switch posts to drafts" decision choice.
 */
export function importedPostSlugsFromJob(job: ImportJobRow | null): string[] {
  const raw = (job?.result as { pages?: unknown } | null)?.pages
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const row of raw) {
    if (row == null || typeof row !== "object") continue
    const r = row as { slug?: unknown; content_type?: unknown; section_count?: unknown; skipped?: unknown; error?: unknown }
    if (
      r.content_type === "post" &&
      typeof r.slug === "string" &&
      typeof r.section_count === "number" &&
      !r.skipped &&
      !r.error
    ) {
      out.push(r.slug)
    }
  }
  return out
}

// === NOS-183: side-by-side review (source screenshot vs imported render) ===

/**
 * NOS-183: slug → hosted source-screenshot URL from a job's result
 * (`result.screenshots`, written at mapping). Empty when the job predates
 * screenshots or every capture failed — the review degrades to single-pane.
 */
export function importScreenshotsFromJob(job: ImportJobRow | null): Record<string, string> {
  const raw = (job?.result as { screenshots?: unknown } | null)?.screenshots
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return {}
  const out: Record<string, string> = {}
  for (const [slug, url] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof url === "string" && /^https?:\/\//.test(url)) out[slug] = url
  }
  return out
}

export interface RewriteImportedSectionResult {
  ok: boolean
  content?: Record<string, unknown>
  error?: string
}

/**
 * NOS-183: "Redo with hint" — rewrite one imported section through the
 * rewrite-section function, grounded in the crawled source page via the
 * import_context payload. Returns the rewritten content (caller applies it to
 * the pending section; already-accepted sections are never touched).
 */
export async function rewriteImportedSection(params: {
  sectionId: string
  orgId: string
  hint: string
  jobId?: string | null
  pageSlug?: string | null
}): Promise<RewriteImportedSectionResult> {
  try {
    const { data: sessWrap } = await supabase.auth.getSession()
    const token = sessWrap.session?.access_token
    const { data, error } = await supabase.functions.invoke("rewrite-section", {
      body: {
        section_id: params.sectionId,
        org_id: params.orgId,
        instruction: params.hint,
        ...(params.jobId && params.pageSlug
          ? { import_context: { job_id: params.jobId, page_slug: params.pageSlug } }
          : {}),
      },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    })
    if (error) {
      return { ok: false, error: error.message ?? "Rewrite failed. Please try again." }
    }
    const payload = data as { content?: unknown; error?: string } | null
    if (payload?.error) return { ok: false, error: payload.error }
    if (payload?.content == null || typeof payload.content !== "object") {
      return { ok: false, error: "Rewrite returned no content. Please try again." }
    }
    return { ok: true, content: payload.content as Record<string, unknown> }
  } catch (err) {
    return {
      ok: false,
      error: errorMessage(err, "Rewrite failed. Please try again."),
    }
  }
}

/** NOS-180: proposed brand from the vision pass (mirrors _shared/brandVision.ts). */
export interface ImportBrandProposal {
  tokens: {
    colors: { primary: string; secondary?: string; accent?: string }
    typography?: { headingFamily?: string; bodyFamily?: string }
  }
  imagery_style?: string[]
  screenshot_url?: string
}

/**
 * NOS-180: the proposed brand tokens from a job's result, or null. Defensive:
 * a proposal without a valid primary color is dropped entirely (never shown).
 */
export function brandProposalFromJob(job: ImportJobRow | null): ImportBrandProposal | null {
  const raw = (job?.result as { brand_proposal?: unknown } | null)?.brand_proposal
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const tokens = r.tokens as Record<string, unknown> | undefined
  const colors = tokens?.colors as Record<string, unknown> | undefined
  const hex = (v: unknown): string | undefined =>
    typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v.trim()) ? v.trim() : undefined
  const primary = hex(colors?.primary)
  if (!primary) return null
  const typography = (tokens?.typography ?? {}) as Record<string, unknown>
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v : undefined
  return {
    tokens: {
      colors: {
        primary,
        ...(hex(colors?.secondary) ? { secondary: hex(colors?.secondary) } : {}),
        ...(hex(colors?.accent) ? { accent: hex(colors?.accent) } : {}),
      },
      ...(str(typography.headingFamily) || str(typography.bodyFamily)
        ? {
            typography: {
              ...(str(typography.headingFamily)
                ? { headingFamily: str(typography.headingFamily) }
                : {}),
              ...(str(typography.bodyFamily) ? { bodyFamily: str(typography.bodyFamily) } : {}),
            },
          }
        : {}),
    },
    imagery_style: Array.isArray(r.imagery_style)
      ? (r.imagery_style as unknown[]).filter((s): s is string => typeof s === "string")
      : undefined,
    screenshot_url: str(r.screenshot_url),
  }
}

/** NOS-190: true when the report has nothing to flag (review stays quiet). */
export function importReportIsClean(report: ImportJobReport): boolean {
  return (
    report.skipped_over_cap.pages === 0 &&
    report.skipped_over_cap.articles === 0 &&
    report.failed_crawls.length === 0 &&
    report.failed_maps.length === 0 &&
    report.image_failure_count === 0 &&
    report.empty_pages.length === 0 &&
    report.protected_drafts.length === 0 &&
    report.unsupported_videos.length === 0 &&
    !report.homepage_missing
  )
}

/** NOS-187: crawl overflow by content type (posts/pages beyond the import caps). */
export interface ImportOverflowSummary {
  articles: number
  pages: number
}

/**
 * NOS-187: per-type overflow, read from the job result (done jobs) or the
 * live crawl state (running jobs). Null when neither carries the breakdown
 * (legacy jobs — callers fall back to the flat pages_overflow count).
 */
export function overflowSummaryFromJob(job: ImportJobRow | null): ImportOverflowSummary | null {
  const fromResult = (job?.result as { overflow_by_type?: unknown } | null)?.overflow_by_type
  const fromState = (job?.state as { overflow_by_type?: unknown } | null)?.overflow_by_type
  const raw = (fromResult ?? fromState) as { article?: unknown; page?: unknown } | undefined
  if (
    raw != null &&
    typeof raw === "object" &&
    typeof raw.article === "number" &&
    typeof raw.page === "number"
  ) {
    return { articles: raw.article, pages: raw.page }
  }
  return null
}

/**
 * NOS-179: the org's most recent import job regardless of status — polls while
 * live, then settles. Lets the builder show both live progress and, once done,
 * the rehost summary (failed image downloads) at review time.
 */
export function useLatestImportJob(
  orgId: string | null,
  pollMs: number = IMPORT_JOB_POLL_MS
): { job: ImportJobRow | null; isActive: boolean; refresh: () => void } {
  const [latestJobId, setLatestJobId] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) {
      setLatestJobId(null)
      return
    }
    let cancelled = false
    supabase
      .from("import_jobs")
      .select("id")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data?.id) setLatestJobId(data.id)
      })
    return () => {
      cancelled = true
    }
  }, [orgId])

  return useImportJob(latestJobId, pollMs)
}
